import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';
import type { ApiEnvelope, ScreenState } from './types';

interface ResourceResult<T> {
  state: ScreenState;
  data: T | null;
  meta: ApiEnvelope<T>['meta'] | null;
  error: ApiError | null;
  reload: () => void;
  /** True once at least one successful load has happened. */
  loaded: boolean;
}

/**
 * Loads a resource and maps the outcome onto one of the design system's screen
 * states. An unavailable resource is never rendered as an empty success.
 */
export function useResource<T>(
  loader: (signal: AbortSignal) => Promise<ApiEnvelope<T>>,
  deps: unknown[] = [],
  options: { emptyWhen?: (data: T) => boolean; searchActive?: boolean } = {},
): ResourceResult<T> {
  const [state, setState] = useState<ScreenState>('LOADING');
  const [data, setData] = useState<T | null>(null);
  const [meta, setMeta] = useState<ApiEnvelope<T>['meta'] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState('LOADING');
    setError(null);

    loaderRef.current(controller.signal)
      .then((envelope) => {
        if (!active) return;
        setData(envelope.data);
        setMeta(envelope.meta);
        setLoaded(true);

        const isEmpty = options.emptyWhen
          ? options.emptyWhen(envelope.data)
          : Array.isArray(envelope.data) && envelope.data.length === 0;

        setState(isEmpty ? (options.searchActive ? 'SEARCH_EMPTY' : 'EMPTY') : 'READY');
      })
      .catch((cause) => {
        if (!active || controller.signal.aborted) return;
        const apiError = cause instanceof ApiError
          ? cause
          : new ApiError(0, 'UNKNOWN', '처리 오류', '-');
        setError(apiError);
        setState(apiError.screenState as ScreenState);
      });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { state, data, meta, error, reload, loaded };
}
